import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Download, Users, Settings, ClipboardList, LayoutDashboard } from 'lucide-react';
import { toast } from 'sonner';
import { fetchClient, fetchClientOnboarding, updateClient, downloadInstaller, downloadUserDeployScript } from '../lib/api';

type TabId = 'overview' | 'users' | 'services' | 'onboarding';

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  onboarding: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400',
  active: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400',
  suspended: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400',
  error: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400',
};

export default function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (!id) return;
    fetchClient(id)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="animate-pulse bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 h-96" />;
  if (!data?.client) return <div className="text-center py-12 text-gray-500">{t('clients.notFound')}</div>;

  const client = data.client;
  const users = data.users || [];
  const tasks = data.onboarding_tasks || [];
  const svcs = (() => { try { return JSON.parse(client.services_json || '{}'); } catch { return {}; } })();

  const tabs: { id: TabId; label: string; icon: React.ReactNode }[] = [
    { id: 'overview', label: t('clients.tabOverview'), icon: <LayoutDashboard className="w-4 h-4" /> },
    { id: 'users', label: t('clients.tabUsers'), icon: <Users className="w-4 h-4" /> },
    { id: 'services', label: t('clients.tabServices'), icon: <Settings className="w-4 h-4" /> },
    { id: 'onboarding', label: t('clients.tabOnboarding'), icon: <ClipboardList className="w-4 h-4" /> },
  ];

  const handleSuspend = async () => {
    const newStatus = client.status === 'suspended' ? 'active' : 'suspended';
    try {
      await updateClient(client.id, { status: newStatus });
      setData({ ...data, client: { ...client, status: newStatus } });
      toast.success(t('clients.statusUpdated'));
    } catch {
      toast.error(t('clients.updateFailed'));
    }
  };

  return (
    <div className="space-y-6">
      <button onClick={() => navigate('/clients')} className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
        <ArrowLeft className="w-4 h-4" /> {t('clients.backToClients')}
      </button>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{client.name}</h1>
          <div className="flex items-center gap-3 mt-1">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[client.status] || STATUS_COLORS.pending}`}>
              {client.status}
            </span>
            {client.industry && <span className="text-sm text-gray-500 dark:text-gray-400">{client.industry}</span>}
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => downloadInstaller(client.id, 'windows').catch(() => toast.error(t('clients.downloadFailed')))}
            className="flex items-center gap-1 px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300">
            <Download className="w-4 h-4" /> Windows
          </button>
          <button onClick={() => downloadInstaller(client.id, 'darwin').catch(() => toast.error(t('clients.downloadFailed')))}
            className="flex items-center gap-1 px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300">
            <Download className="w-4 h-4" /> macOS
          </button>
          <button onClick={handleSuspend}
            className={`px-3 py-2 rounded-lg text-sm font-medium ${client.status === 'suspended' ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-yellow-500 text-white hover:bg-yellow-600'}`}>
            {client.status === 'suspended' ? t('clients.reactivate') : t('clients.suspend')}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700">
        {tabs.map((tab) => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === tab.id
                ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}>
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-2 gap-4">
          {[
            { label: t('clients.contactEmail'), value: client.contact_email },
            { label: 'Fleet Team', value: client.fleet_team_name || (client.fleet_team_id != null ? `#${client.fleet_team_id}` : '-') },
            { label: 'Wazuh Group', value: client.wazuh_group || '-' },
            { label: 'Zitadel Org', value: client.zitadel_org_id || '-' },
            { label: 'SecuScan Tenant', value: client.secuscan_tenant_id || '-' },
            { label: 'vCISO Org', value: client.vciso_org_id || '-' },
          ].map(({ label, value }) => (
            <div key={label} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
              <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mt-1">{value || '-'}</p>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'users' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          {users.length === 0 ? (
            <p className="p-8 text-center text-gray-500 dark:text-gray-400">{t('clients.noUsers')}</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left px-4 py-3 font-medium text-gray-500">{t('clients.email')}</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">{t('clients.name')}</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">{t('clients.role')}</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">{t('clients.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {users.map((u: any) => (
                  <tr key={u.id}>
                    <td className="px-4 py-3 text-gray-900 dark:text-gray-100">{u.email}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{u.first_name} {u.last_name}</td>
                    <td className="px-4 py-3"><span className="px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-xs">{u.role}</span></td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => downloadUserDeployScript(client.id, u.id).catch(() => toast.error(t('clients.scriptDownloadFailed')))}
                        className="flex items-center gap-1 px-2 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded text-xs hover:bg-blue-100 dark:hover:bg-blue-900/50"
                      >
                        <Download className="w-3 h-3" /> {t('clients.downloadScript')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'services' && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {Object.entries(svcs).map(([key, enabled]) => (
            <div key={key} className={`p-4 rounded-xl border-2 ${enabled ? 'border-green-500 bg-green-50 dark:bg-green-900/10' : 'border-gray-200 dark:border-gray-700'}`}>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{key}</p>
              <p className={`text-xs mt-1 ${enabled ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`}>
                {enabled ? t('clients.enabled') : t('clients.disabled')}
              </p>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'onboarding' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-3">
          {tasks.length === 0 ? (
            <p className="text-center text-gray-500 dark:text-gray-400">{t('clients.noOnboarding')}</p>
          ) : tasks.map((task: any, i: number) => (
            <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
              <div className="flex items-center gap-3">
                <span className={`w-2 h-2 rounded-full ${
                  task.status === 'completed' ? 'bg-green-500' :
                  task.status === 'running' ? 'bg-blue-500 animate-pulse' :
                  task.status === 'failed' ? 'bg-red-500' : 'bg-gray-300'
                }`} />
                <span className="text-sm text-gray-900 dark:text-gray-100">{task.step.replace(/_/g, ' ')}</span>
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400">{task.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
